// Reader-booking pricing — mirrors the backend engine in app.js
// (resolveReaderBookingPricing / getReaderBookingMemberState). The backend
// recomputes and enforces this at checkout, so it is authoritative; this module
// exists only so the UI can show the talent what they'll pay. The two MUST agree.
//
// Model:
//  - Base (member) price = the reader's rate × 15-min blocks. Reader always nets
//    90% of the base.
//  - Non-members (and members past their monthly allotment) pay a flat $5 pad on
//    top. The pad is 100% Hey Joe's.
//  - Active Plus / day-pass members get the pad waived on their included monthly
//    reader sessions (readerSessionsRemaining > 0).

export const NON_MEMBER_PAD = 5; // dollars — mirrors NON_MEMBER_PAD_CENTS (500)

// Tiers that count as paid Plus / day-pass members — the same set the backend
// treats as `canUseTools`. Free creator/reader tiers do NOT get the pad waived.
export const PAID_MEMBER_TIERS = ['day_pass', 'monthly', 'yearly', 'cd_plus'];

export interface MemberState {
  isSubscriber: boolean;
  tier?: string;
  sessionsRemaining: number;
}

export function isPlusMember(m: MemberState): boolean {
  return !!m.isSubscriber && !!m.tier && PAID_MEMBER_TIERS.includes(m.tier);
}

export interface BookingPrice {
  baseCost: number;    // dollars — reader's rate × blocks (the member price)
  pad: number;         // dollars added on top ($5 or 0)
  padApplied: boolean;
  total: number;       // dollars the talent pays
  readerEarns: number; // dollars the reader nets (90% of base)
  padWaived: boolean;  // member skipped the pad using an included session
}

// baseRate = per-15-min rate in dollars; blocks = sessionLength / 15.
export function computeBookingPrice(baseRate: number, blocks: number, m: MemberState): BookingPrice {
  const baseCost = +(baseRate * blocks).toFixed(2);
  const padWaived = isPlusMember(m) && m.sessionsRemaining > 0;
  const pad = padWaived ? 0 : NON_MEMBER_PAD;
  return {
    baseCost,
    pad,
    padApplied: pad > 0,
    total: +(baseCost + pad).toFixed(2),
    readerEarns: +(baseCost * 0.9).toFixed(2),
    padWaived,
  };
}
